import React, { useState } from "react";
import { HelpCircle, ChevronRight, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CallbackHelp from "@/components/CallbackHelp";

type FAQTopic = "amount" | "tenure" | null;

interface FAQItem {
  id: FAQTopic;
  question: string;
  icon: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    id: "amount",
    question: "What is the mandate amount?",
    icon: "💰",
  },
  {
    id: "tenure",
    question: "What is the mandate tenure?",
    icon: "📅",
  },
];

const FAQ_CONTENT: Record<
  NonNullable<FAQTopic>,
  { title: string; sections: { heading: string; body: string }[] }
> = {
  amount: {
    title: "What is the Mandate Amount?",
    sections: [
      {
        heading: "What it means",
        body: "The mandate amount is the maximum amount your bank is authorised to deduct automatically each month for your loan repayment. Think of it as the upper limit your bank has been given permission to collect on your behalf.",
      },
      {
        heading: "Why is it higher than my EMI?",
        body: "Your actual monthly EMI (Equated Monthly Instalment) is the fixed amount you pay each month. However, the mandate amount is set slightly higher than your EMI as a safety buffer. This extra headroom ensures that your repayments go through smoothly even if:",
      },
      {
        heading: "Interest rate changes",
        body: "If your loan has a floating interest rate, your EMI may increase slightly over time. The buffer ensures the mandate can still cover the revised amount without needing to be re-registered.",
      },
      {
        heading: "Late payment charges",
        body: "If a payment is delayed, a small late fee may be added to your next deduction. The higher mandate amount ensures this can be collected without any disruption.",
      },
      {
        heading: "Additional fees or adjustments",
        body: "Occasionally, minor processing fees or adjustments may be applied to your account. The mandate buffer covers these without requiring you to take any extra steps.",
      },
      {
        heading: "Your money is safe",
        body: "Only the exact amount due will ever be deducted — the mandate amount is simply the ceiling, not what gets charged every month. You will always be notified before any deduction is made.",
      },
    ],
  },
  tenure: {
    title: "What is the Mandate Tenure?",
    sections: [
      {
        heading: "What it means",
        body: "The mandate tenure is the period during which your bank is authorised to make automatic deductions for your loan repayments. It defines how long the repayment instruction remains active on your bank account.",
      },
      {
        heading: "Why is it longer than my loan tenure?",
        body: "Your loan tenure is the agreed repayment period for your loan. The mandate tenure is set a bit longer than this as a practical safeguard. Here is why:",
      },
      {
        heading: "Grace periods and processing delays",
        body: "Loan closures involve several administrative steps — final settlement calculations, NOC (No Objection Certificate) issuance, and system updates. These can take a few extra days or weeks beyond your last EMI date.",
      },
      {
        heading: "Extended repayment scenarios",
        body: "In some cases, a missed payment or restructuring may extend your repayment schedule slightly. The longer mandate tenure ensures collections can continue without interruption.",
      },
      {
        heading: "Avoiding re-registration hassle",
        body: "If the mandate expired exactly on your last EMI date and there was even a small delay in closure, you would need to re-register the mandate — a time-consuming process. The extended tenure prevents this entirely.",
      },
      {
        heading: "No extra charges after loan closure",
        body: "Once your loan is fully repaid and closed, no further deductions will be made — even if the mandate is technically still active. The mandate simply becomes inactive after the loan account is settled.",
      },
    ],
  },
};

interface MandateFAQModalProps {
  /** Controls whether the FAQ entry point (the subtle link) is rendered */
  show?: boolean;
  /**
   * "default" — subtle gray link (used inside mandate cards, SetupMandates).
   * "amber"   — amber-toned link for use inside amber-background containers
   *             (e.g. ChangeBankDetailsLink).
   */
  variant?: "default" | "amber";
}

const MandateFAQModal: React.FC<MandateFAQModalProps> = ({
  show = true,
  variant = "default",
}) => {
  const [open, setOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState<FAQTopic>(null);
  const [supportOpen, setSupportOpen] = useState(false);

  const handleQuestionClick = (id: FAQTopic) => {
    setActiveTopic(id);
  };

  const handleBack = () => {
    setActiveTopic(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Reset to question list when dialog closes
      setTimeout(() => setActiveTopic(null), 200);
    }
  };

  if (!show) return null;

  const activeContent = activeTopic ? FAQ_CONTENT[activeTopic] : null;

  return (
    <>
      {/* FAQ trigger link — style varies by variant */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          variant === "amber"
            ? "inline-flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 transition-colors duration-200 group"
            : "inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-intellend-green transition-colors duration-200 group"
        }
        aria-label="Open frequently asked questions about mandates"
      >
        <HelpCircle
          className={
            variant === "amber"
              ? "h-3.5 w-3.5 text-amber-600 group-hover:text-amber-900 transition-colors duration-200"
              : "h-3.5 w-3.5 text-gray-400 group-hover:text-intellend-green transition-colors duration-200"
          }
        />
        <span className="underline underline-offset-2 decoration-dotted">
          Have questions about mandates?
        </span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl">
          {/* Header */}
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-3">
              {activeTopic && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors duration-150 text-gray-500 hover:text-gray-700 shrink-0"
                  aria-label="Back to questions list"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-intellend-green/10 p-1.5 rounded-full shrink-0">
                  <HelpCircle className="h-4 w-4 text-intellend-green" />
                </div>
                <DialogTitle className="text-base font-semibold text-gray-800 truncate">
                  {activeTopic ? activeContent?.title : "Mandate FAQs"}
                </DialogTitle>
              </div>
            </div>
            {!activeTopic && (
              <p className="text-xs text-gray-500 mt-1 ml-0 pl-0">
                Click a question below to learn more.
              </p>
            )}
          </DialogHeader>

          {/* Body — scrollable */}
          <div className="overflow-y-auto flex-1 overscroll-contain">
            {/* Question list view */}
            {!activeTopic && (
              <ul className="divide-y divide-gray-100">
                {FAQ_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleQuestionClick(item.id)}
                      className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 group"
                    >
                      <span className="text-xl shrink-0" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-intellend-green transition-colors duration-150">
                        {item.question}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-intellend-green shrink-0 transition-colors duration-150" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Detail view */}
            {activeTopic && activeContent && (
              <div className="px-5 py-5 space-y-4 animate-in fade-in-0 slide-in-from-right-4 duration-200">
                {activeContent.sections.map((section, index) => (
                  <div key={index}>
                    <h3 className="text-sm font-semibold text-intellend-green mb-1">
                      {section.heading}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {section.body}
                    </p>
                  </div>
                ))}

                {/* Reassurance footer */}
                <div className="mt-5 bg-intellend-green/5 border border-intellend-green/20 rounded-xl p-4">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {activeTopic === "amount"
                      ? "💡 The mandate amount is a safeguard, not an extra charge. You will only ever be debited the exact amount due on your loan."
                      : "💡 The extended mandate tenure is purely administrative. Your loan account will be closed on time, and no deductions will occur after full repayment."}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 shrink-0 bg-gray-50/60">
            <p className="text-[11px] text-gray-400 text-center">
              Still have questions?{" "}
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                className="text-intellend-green underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                Contact support
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <CallbackHelp
        isOpen={supportOpen}
        setIsOpen={setSupportOpen}
        showOk={true}
        showWhatsappUs={true}
        showCallUs={true}
        title="Contact Support"
        description="Our team is here to help you with any questions about your mandate setup."
      />
    </>
  );
};

export default MandateFAQModal;
